import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import {
  fetchAccountsSilent,
  setAccountEditing,
  useGeneralSettingsAccounts,
  useGeneralSettingsEditAccountResult,
} from '../generalSettingsSlice';
import { ContentsHeader } from 'components/ContentsHeader';
import msgId from 'resources/intl';
import { Search as SearchIcon } from '@mui/icons-material';
import colors from 'resources/colors';
import { NormalButton } from 'components/NormalButton';
import { DataGrid, Column, FilterRow, Scrolling, Selection } from 'devextreme-react/data-grid';
import dimens from 'resources/dimens';
import { DxDataGridColumn } from 'components/DxDataGrid';
import constants from 'resources/constants';
import {
  Account,
  AuthorityAdministrators,
  AuthorityAdminUsers,
  AuthorityDevelopers,
  AuthorityGuests,
  AuthorityOperators,
  AuthorityUsers,
} from 'models/accounts';
import { AccountAuthorityEditDialog } from './AccountAuthorityEditDialog';
import config from 'resources/config';
import dayjs from 'dayjs';
import {
  useAdminSettingsEditAccountGroupResult,
  useAdminSettingsEditAccountResult,
} from 'features/contents/adminSettings/adminSettingsSlice';
import { RowPreparedEvent } from 'devextreme/ui/data_grid';
import { getTodayLimit } from 'utils/misc';

interface AccountManagementProps {
  open: boolean;
  height: number;
}

export function AccountManagement(props: AccountManagementProps): JSX.Element {
  const { open, height } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const accounts = useGeneralSettingsAccounts();
  const accountResult = useGeneralSettingsEditAccountResult();
  const adminAccountResult = useAdminSettingsEditAccountResult();
  const adminAccountGroupResult = useAdminSettingsEditAccountGroupResult();
  const dataGridRef = useRef<DataGrid<Account, unknown>>(null);
  const minDate = dayjs(config.minDate).toDate();
  const maxDate = dayjs(getTodayLimit()).toDate();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const authorityNameCalculateCellValue = (rowData: any) => {
    const account: Account = rowData;
    switch (account.authorityId) {
      case AuthorityAdministrators:
        return intl.formatMessage({ id: msgId.authorityAdminitrators });
      case AuthorityDevelopers:
        return intl.formatMessage({ id: msgId.authorityDevelopers });
      case AuthorityOperators:
        return intl.formatMessage({ id: msgId.authorityOperators });
      case AuthorityAdminUsers:
        return intl.formatMessage({ id: msgId.authorityAdminUsers });
      case AuthorityUsers:
        return intl.formatMessage({ id: msgId.authorityUsers });
      case AuthorityGuests:
        return intl.formatMessage({ id: msgId.authorityGuests });
      default:
        return '';
    }
  };

  const columns: DxDataGridColumn[] = [
    {
      key: 'cognitoAccountId',
      dataField: 'cognitoAccountId',
      caption: intl.formatMessage({ id: msgId.accountName }),
      defaultSortOrder: 'asc',
    },
    {
      key: 'authorityName',
      dataField: 'authorityName',
      caption: intl.formatMessage({ id: msgId.authority }),
      calculateCellValue: authorityNameCalculateCellValue,
      width: 200,
    },
    {
      key: 'updatedAt',
      dataField: 'updatedAt',
      dataType: 'datetime',
      filterOperations: constants.dataGrid.dateFilterOperations,
      format: constants.dateFormat.yyyyMMddHHmm,
      caption: intl.formatMessage({ id: msgId.updatedAt }),
      width: 160,
      editorOptions: { min: minDate, max: maxDate },
    },
  ];

  /**
   * 行選択
   * @param account アカウント
   */
  const handleSelectionChanged = (account: Account) => {
    dataGridRef.current?.instance.clearSelection();
    if (
      account != null &&
      (account.authorityId == AuthorityAdminUsers || account.authorityId == AuthorityUsers)
    ) {
      dispatch(setAccountEditing({ editing: true, account: account }));
    }
  };

  /**
   * フィルターリセット
   */
  const handleFilterResetClick = () => {
    if (dataGridRef.current) {
      const dataGrid = dataGridRef.current.instance;
      columns.forEach((c) => {
        dataGrid.columnOption(c.dataField, 'selectedFilterOperation', null);
      });
      dataGrid.clearFilter();
    }
  };

  // 日付入力のフィルターをreadonlyにする。
  const setInputReadOnly = () => {
    const element = document.getElementById('generalAccountDataGrid');
    if (element) {
      const elements = [3];
      elements.forEach((colIndex) => {
        const dateFilterInput = element.querySelector(
          '.dx-datagrid-filter-row td[aria-colindex="' + colIndex + '"]'
        ) as HTMLElement;
        if (dateFilterInput) {
          const inputElement = dateFilterInput.querySelector(
            '.dx-texteditor-input'
          ) as HTMLInputElement;
          if (inputElement) {
            inputElement.readOnly = true;
          }
        }
      });
    }
  };

  /**
   * 行の生成時に呼ばれる。
   */
  const handleRowPrepared = (e: RowPreparedEvent<Account, unknown>) => {
    if (e.rowType === 'data') {
      if (e.data.authorityId !== AuthorityAdminUsers && e.data.authorityId !== AuthorityUsers) {
        const style = `background-color: ${colors.listItem.disabled};`;
        e.rowElement.setAttribute('style', style);
      }
    }
  };

  /**
   * API完了後Account情報を再取得
   */
  useEffect(() => {
    if (open && accountResult && accountResult.isSuccess) {
      dispatch(fetchAccountsSilent());
    }
  }, [dispatch, open, accountResult]);

  useEffect(() => {
    if (adminAccountResult && adminAccountResult.isSuccess) {
      dispatch(fetchAccountsSilent());
    }
  }, [dispatch, adminAccountResult]);

  useEffect(() => {
    if (adminAccountGroupResult && adminAccountGroupResult.isSuccess) {
      dispatch(fetchAccountsSilent());
    }
  }, [dispatch, adminAccountGroupResult]);

  return (
    <Box
      sx={{
        position: 'absolute',
        width: '100%',
        background: colors.subDrawer.background,
        visibility: open ? 'visible' : 'hidden',
      }}
    >
      {accounts && (
        <React.Fragment>
          <ContentsHeader title={intl.formatMessage({ id: msgId.accountAuthorityManagement })}>
            <NormalButton
              label={intl.formatMessage({ id: msgId.filterReset })}
              color="primary"
              startIcon={<SearchIcon />}
              onClick={() => handleFilterResetClick()}
            />
          </ContentsHeader>
          <DataGrid
            id="generalAccountDataGrid"
            style={{ margin: dimens.dataGrid.margin }}
            ref={dataGridRef}
            height={height - dimens.dataGrid.offsetHeight}
            dataSource={accounts}
            showBorders={true}
            allowColumnResizing={true}
            hoverStateEnabled={true}
            columnResizingMode="nextColumn"
            keyExpr="cognitoAccountId"
            columnAutoWidth={true}
            noDataText={intl.formatMessage({ id: msgId.noResults })}
            onSelectionChanged={(e) => handleSelectionChanged(e.selectedRowsData[0])}
            onCellClick={setInputReadOnly}
            onRowPrepared={handleRowPrepared}
          >
            <Selection mode="single" />
            <FilterRow visible={true} />
            <Scrolling mode="virtual" />
            {columns.map((column) => {
              return (
                <Column
                  key={column.key}
                  name={column.key}
                  dataField={column.dataField}
                  minWidth={dimens.dataGrid.column.minWidth}
                  dataType={column.dataType}
                  format={column.format}
                  caption={column.caption}
                  width={column.width}
                  filterOperations={column.filterOperations}
                  defaultSortOrder={column.defaultSortOrder}
                  editorOptions={column.editorOptions}
                  calculateCellValue={column.calculateCellValue}
                />
              );
            })}
          </DataGrid>
        </React.Fragment>
      )}
      <AccountAuthorityEditDialog contentHeight={height} />
    </Box>
  );
}
